import type { RootState } from '../reducers';

import type { BaseAction } from '../@types/actions';

import * as http from '../Http';
import requestCache from '../utils/request-cache';

import type { Dispatch } from '@reduxjs/toolkit';
import type {
  AssignmentDraft,
  AssignmentDraftsResponse,
  AssignmentUpdateDraftResponse,
} from '../@types/assignment';
import {
  buildAuthorizedDeleteRequest,
  buildAuthorizedGetRequest,
  buildAuthorizedPostRequest,
  buildAuthorizedPutRequest,
} from '../Http/requestTemplates';

export const REQ_ASSIGNMENT_DRAFTS_LIST = 'REQ/ASSIGNMENT_DRAFTS_LIST';
export const RCV_ASSIGNMENT_DRAFTS_LIST = 'RCV/ASSIGNMENT_DRAFTS_LIST';
export const ERR_ASSIGNMENT_DRAFTS_LIST = 'ERR/ASSIGNMENT_DRAFTS_LIST';

export const REQ_ASSIGNMENT_DRAFT = 'REQ/ASSIGNMENT_DRAFT';
export const RCV_ASSIGNMENT_DRAFT = 'RCV/ASSIGNMENT_DRAFT';
export const ERR_ASSIGNMENT_DRAFT = 'ERR/ASSIGNMENT_DRAFT';

export const REQ_UPDATE_ASSIGNMENT_DRAFT = 'REQ/UPDATE_ASSIGNMENT_DRAFT';
export const RCV_UPDATE_ASSIGNMENT_DRAFT = 'RCV/UPDATE_ASSIGNMENT_DRAFT';
export const ERR_UPDATE_ASSIGNMENT_DRAFT = 'ERR/UPDATE_ASSIGNMENT_DRAFT';

export const REQ_REMOVE_ASSIGNMENT_DRAFT = 'REQ/REMOVE_ASSIGNMENT_DRAFT';
export const RCV_REMOVE_ASSIGNMENT_DRAFT = 'RCV/REMOVE_ASSIGNMENT_DRAFT';
export const ERR_REMOVE_ASSIGNMENT_DRAFT = 'ERR/REMOVE_ASSIGNMENT_DRAFT';

export const REQ_CREATE_ASSIGNMENT_DRAFT = 'REQ/CREATE_ASSIGNMENT_DRAFT';
export const RCV_CREATE_ASSIGNMENT_DRAFT = 'RCV/CREATE_ASSIGNMENT_DRAFT';
export const ERR_CREATE_ASSIGNMENT_DRAFT = 'ERR/CREATE_ASSIGNMENT_DRAFT';

export const REQ_PUBLISH_ASSIGNMENT_DRAFT = 'REQ/PUBLISH_ASSIGNMENT_DRAFT';
export const RCV_PUBLISH_ASSIGNMENT_DRAFT = 'RCV/PUBLISH_ASSIGNMENT_DRAFT';
export const ERR_PUBLISH_ASSIGNMENT_DRAFT = 'ERR/PUBLISH_ASSIGNMENT_DRAFT';

export const REQ_SET_UNSAVED = 'REQ/SET_UNSAVED';

export const REQ_CLEAR_CREATED_ASSIGNMENT_DRAFT = 'REQ/CLEAR_CREATED_ASSIGNMENT_DRAFT';

export type REQ_ASSIGNMENT_DRAFTS_LIST_TYPE = BaseAction<typeof REQ_ASSIGNMENT_DRAFTS_LIST>;
export type RCV_ASSIGNMENT_DRAFTS_LIST_TYPE = BaseAction<typeof RCV_ASSIGNMENT_DRAFTS_LIST> & {
  response: AssignmentDraftsResponse;
};
export type ERR_ASSIGNMENT_DRAFTS_LIST_TYPE = BaseAction<typeof ERR_ASSIGNMENT_DRAFTS_LIST>;

export type REQ_CREATE_ASSIGNMENT_DRAFT_TYPE = BaseAction<typeof REQ_CREATE_ASSIGNMENT_DRAFT>;
export type RCV_CREATE_ASSIGNMENT_DRAFT_TYPE = BaseAction<typeof RCV_CREATE_ASSIGNMENT_DRAFT> & {
  response: AssignmentUpdateDraftResponse;
};
export type ERR_CREATE_ASSIGNMENT_DRAFT_TYPE = BaseAction<typeof ERR_CREATE_ASSIGNMENT_DRAFT>;

export type REQ_ASSIGNMENT_DRAFT_TYPE = BaseAction<typeof REQ_ASSIGNMENT_DRAFT>;
export type RCV_ASSIGNMENT_DRAFT_TYPE = BaseAction<typeof RCV_ASSIGNMENT_DRAFT> & {
  response: AssignmentDraft;
};
export type ERR_ASSIGNMENT_DRAFT_TYPE = BaseAction<typeof ERR_ASSIGNMENT_DRAFT>;

export type REQ_PUBLISH_ASSIGNMENT_DRAFT_TYPE = BaseAction<typeof REQ_PUBLISH_ASSIGNMENT_DRAFT>;
export type RCV_PUBLISH_ASSIGNMENT_DRAFT_TYPE = BaseAction<typeof RCV_PUBLISH_ASSIGNMENT_DRAFT> & {
  response: AssignmentDraft;
};
export type ERR_PUBLISH_ASSIGNMENT_DRAFT_TYPE = BaseAction<typeof ERR_PUBLISH_ASSIGNMENT_DRAFT>;

export type REQ_UPDATE_ASSIGNMENT_DRAFT_TYPE = BaseAction<typeof REQ_UPDATE_ASSIGNMENT_DRAFT>;
export type RCV_UPDATE_ASSIGNMENT_DRAFT_TYPE = BaseAction<typeof RCV_UPDATE_ASSIGNMENT_DRAFT> & {
  response: AssignmentDraft;
};
export type ERR_UPDATE_ASSIGNMENT_DRAFT_TYPE = BaseAction<typeof ERR_UPDATE_ASSIGNMENT_DRAFT>;

export type REQ_REMOVE_ASSIGNMENT_DRAFT_TYPE = BaseAction<typeof REQ_REMOVE_ASSIGNMENT_DRAFT>;
export type RCV_REMOVE_ASSIGNMENT_DRAFT_TYPE = BaseAction<typeof RCV_REMOVE_ASSIGNMENT_DRAFT> & {
  response: string;
};
export type ERR_REMOVE_ASSIGNMENT_DRAFT_TYPE = BaseAction<typeof ERR_REMOVE_ASSIGNMENT_DRAFT>;

export type REQ_SET_UNSAVED_TYPE = BaseAction<typeof REQ_SET_UNSAVED> & {
  unsaved: boolean;
};

export type REQ_CLEAR_CREATED_ASSIGNMENT_DRAFT_TYPE = BaseAction<
  typeof REQ_CLEAR_CREATED_ASSIGNMENT_DRAFT
>;

export type ASSIGNMENT_DRAFT_ACTIONS =
  | REQ_ASSIGNMENT_DRAFTS_LIST_TYPE
  | RCV_ASSIGNMENT_DRAFTS_LIST_TYPE
  | ERR_ASSIGNMENT_DRAFTS_LIST_TYPE
  | REQ_CREATE_ASSIGNMENT_DRAFT_TYPE
  | RCV_CREATE_ASSIGNMENT_DRAFT_TYPE
  | ERR_CREATE_ASSIGNMENT_DRAFT_TYPE
  | REQ_ASSIGNMENT_DRAFT_TYPE
  | RCV_ASSIGNMENT_DRAFT_TYPE
  | ERR_ASSIGNMENT_DRAFT_TYPE
  | REQ_PUBLISH_ASSIGNMENT_DRAFT_TYPE
  | RCV_PUBLISH_ASSIGNMENT_DRAFT_TYPE
  | ERR_PUBLISH_ASSIGNMENT_DRAFT_TYPE
  | REQ_UPDATE_ASSIGNMENT_DRAFT_TYPE
  | RCV_UPDATE_ASSIGNMENT_DRAFT_TYPE
  | ERR_UPDATE_ASSIGNMENT_DRAFT_TYPE
  | REQ_REMOVE_ASSIGNMENT_DRAFT_TYPE
  | RCV_REMOVE_ASSIGNMENT_DRAFT_TYPE
  | ERR_REMOVE_ASSIGNMENT_DRAFT_TYPE
  | REQ_SET_UNSAVED_TYPE
  | REQ_CLEAR_CREATED_ASSIGNMENT_DRAFT_TYPE;

export const getAllDrafts = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const { auth } = getState();
  if (!auth.token || requestCache.isPending(REQ_ASSIGNMENT_DRAFTS_LIST)) {
    return;
  }
  requestCache.isPending(REQ_ASSIGNMENT_DRAFTS_LIST, true);

  try {
    dispatch({ type: REQ_ASSIGNMENT_DRAFTS_LIST });

    const response = await http.json<AssignmentDraftsResponse>(
      '/api/assignments/draft',
      buildAuthorizedGetRequest(auth.token),
    );
    http.validateResponse(response);

    dispatch({
      type: RCV_ASSIGNMENT_DRAFTS_LIST,
      response: response.response,
    });
  } catch (_e) {
    dispatch({ type: ERR_ASSIGNMENT_DRAFTS_LIST });
  } finally {
    requestCache.isPending(REQ_ASSIGNMENT_DRAFTS_LIST, false);
  }
};

export const getDraft = (id: string) => async (dispatch: Dispatch, getState: () => RootState) => {
  const { auth } = getState();

  if (!auth.token || requestCache.isPending(REQ_ASSIGNMENT_DRAFT)) {
    return;
  }
  requestCache.isPending(REQ_ASSIGNMENT_DRAFT, true);

  try {
    dispatch({ type: REQ_ASSIGNMENT_DRAFT });

    const response = await http.json<AssignmentDraft>(
      `/api/assignments/draft/${id}`,
      buildAuthorizedGetRequest(auth.token),
    );
    http.validateResponse(response);

    dispatch({
      type: RCV_ASSIGNMENT_DRAFT,
      response: response.response,
    });
  } catch (_e) {
    dispatch({ type: ERR_ASSIGNMENT_DRAFT });
  } finally {
    requestCache.isPending(REQ_ASSIGNMENT_DRAFT, false);
  }
};

export const createDraft =
  (assignment: AssignmentDraft) => async (dispatch: Dispatch, getState: () => RootState) => {
    const { auth } = getState();

    if (!auth.token || requestCache.isPending(REQ_CREATE_ASSIGNMENT_DRAFT)) {
      return;
    }
    requestCache.isPending(REQ_CREATE_ASSIGNMENT_DRAFT, true);

    try {
      dispatch({ type: REQ_CREATE_ASSIGNMENT_DRAFT });

      const response = await http.json<AssignmentDraft>(
        '/api/assignments/draft',
        buildAuthorizedPostRequest(auth.token, { content: assignment }),
      );
      http.validateResponse(response);

      dispatch({
        type: RCV_CREATE_ASSIGNMENT_DRAFT,
        response: response.response,
      });
    } catch (_e) {
      dispatch({ type: ERR_CREATE_ASSIGNMENT_DRAFT });
    } finally {
      requestCache.isPending(REQ_CREATE_ASSIGNMENT_DRAFT, false);
    }
  };

export const removeDraft =
  (id: string) => async (dispatch: Dispatch, getState: () => RootState) => {
    const { auth } = getState();
    if (!auth.token || requestCache.isPending(REQ_REMOVE_ASSIGNMENT_DRAFT)) {
      return;
    }
    requestCache.isPending(REQ_REMOVE_ASSIGNMENT_DRAFT, true);

    try {
      dispatch({ type: REQ_REMOVE_ASSIGNMENT_DRAFT });

      const response = await http.json<AssignmentDraft>(
        `/api/assignments/draft/${id}`,
        buildAuthorizedDeleteRequest(auth.token),
      );
      http.validateResponse(response);

      dispatch({
        type: RCV_REMOVE_ASSIGNMENT_DRAFT,
        response: response.response,
      });
    } catch (_e) {
      dispatch({ type: ERR_REMOVE_ASSIGNMENT_DRAFT });
    } finally {
      requestCache.isPending(REQ_REMOVE_ASSIGNMENT_DRAFT, false);
    }
  };

export const updateDraft =
  (assignment: AssignmentDraft, id: string) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const { auth } = getState();

    if (!auth.token || requestCache.isPending(REQ_UPDATE_ASSIGNMENT_DRAFT)) {
      return;
    }
    requestCache.isPending(REQ_UPDATE_ASSIGNMENT_DRAFT, true);

    try {
      dispatch({ type: REQ_UPDATE_ASSIGNMENT_DRAFT });

      const response = await http.json<AssignmentDraft>(
        `/api/assignments/draft/${id}`,
        buildAuthorizedPutRequest(auth.token, { content: assignment }),
      );
      http.validateResponse(response);

      dispatch({
        type: RCV_UPDATE_ASSIGNMENT_DRAFT,
        response: response.response,
      });
    } catch (_e) {
      dispatch({ type: ERR_UPDATE_ASSIGNMENT_DRAFT });
    } finally {
      requestCache.isPending(REQ_UPDATE_ASSIGNMENT_DRAFT, false);
    }
  };

export const publishDraft =
  (assignment: AssignmentDraft, _id: string) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const { auth } = getState();
    if (!auth.token || requestCache.isPending(REQ_PUBLISH_ASSIGNMENT_DRAFT)) {
      return;
    }
    requestCache.isPending(REQ_PUBLISH_ASSIGNMENT_DRAFT, true);

    try {
      dispatch({ type: REQ_PUBLISH_ASSIGNMENT_DRAFT });

      const response = await http.json<null>(
        `/api/assignments/draft/publish/${_id}`,
        buildAuthorizedPostRequest(auth.token, { content: assignment }),
      );
      http.validateResponse(response);

      dispatch({
        type: RCV_PUBLISH_ASSIGNMENT_DRAFT,
        response: { ...assignment, id: _id },
      });
    } catch (_e) {
      dispatch({ type: ERR_PUBLISH_ASSIGNMENT_DRAFT });
    } finally {
      requestCache.isPending(REQ_PUBLISH_ASSIGNMENT_DRAFT, false);
    }
  };

export const setUnsaved = (unsaved: boolean) => (dispatch: Dispatch) =>
  dispatch({ type: REQ_SET_UNSAVED, unsaved });

export const resetCreatedDraftItem = () => (dispatch: Dispatch) =>
  dispatch({ type: REQ_CLEAR_CREATED_ASSIGNMENT_DRAFT });
